import React, { ReactNode, useCallback, useEffect } from "react";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { useTranslation } from "lib/i18n/use-translation";
import { IconButton } from "components/ui";
import { trackEvent } from "utility/mixpanel";
import { useHorizontalScroll } from "hooks/use-horizontal-scroll";
import { useInViews } from "utility/useInViews";

import styles from "./styles.module.scss";

interface CarouselButtonGroupProps {
  onPrev: () => void;
  onNext: () => void;
  canScrollLeft?: boolean;
  canScrollRight?: boolean;
}

enum CarouselEventName {
  ButtonClick = "Arrow Clicked",
}

enum CarouselEventPropertyKeys {
  ListName = "List Name",
  Direction = "Direction",
}

enum CarouselEventPropertyValues {
  DirectionLeft = "left",
  DirectionRight = "right",
}

const CarouselButtonGroup: React.FC<CarouselButtonGroupProps> = ({
  onPrev,
  onNext,
  canScrollLeft = false,
  canScrollRight = false,
}) => {
  const { t } = useTranslation(["common"]);

  return (
    <div className={styles["carousel-button-group"]}>
      <IconButton
        className={styles["carousel-button"]}
        aria-label={t("common:a11y.scrollLeft")}
        onClick={onPrev}
        disabled={!canScrollLeft}
        icon={<ChevronIcon className={styles["previous-chevron"]} />}
      />
      <IconButton
        className={styles["carousel-button"]}
        aria-label={t("common:a11y.scrollRight")}
        onClick={onNext}
        disabled={!canScrollRight}
        icon={<ChevronIcon />}
      />
    </div>
  );
};

interface CarouselProps {
  items: ReactNode[];
  title?: ReactNode;
  offsetOverride?: number;
  showCarouselButtons?: boolean;
  loading?: boolean;
  error?: boolean;
  listName?: string;
}

export const Carousel: React.FC<CarouselProps> = ({
  title,
  items,
  offsetOverride,
  showCarouselButtons = true,
  loading = false,
  error = false,
  listName,
}) => {
  const {
    containerRef: scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollBy,
  } = useHorizontalScroll({ deps: [items] });

  const { ref, inView } = useInViews();
  const { t } = useTranslation(["components", "common"]);

  const logImpression = useCallback(() => {
    trackEvent("Home Page Carousel Impression", {});
  }, []);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  const handleScrollLeft = () => {
    scrollBy(
      offsetOverride
        ? -offsetOverride
        : -scrollContainerRef.current!.clientWidth * 0.8
    );
    trackEvent(CarouselEventName.ButtonClick, {
      [CarouselEventPropertyKeys.ListName]: listName,
      [CarouselEventPropertyKeys.Direction]:
        CarouselEventPropertyValues.DirectionLeft,
    });
  };

  const handleScrollRight = () => {
    scrollBy(offsetOverride ?? scrollContainerRef.current!.clientWidth * 0.8);
    trackEvent(CarouselEventName.ButtonClick, {
      [CarouselEventPropertyKeys.ListName]: listName,
      [CarouselEventPropertyKeys.Direction]:
        CarouselEventPropertyValues.DirectionRight,
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles["loading-container"]}>
          <div
            className="spinner-border"
            role="status"
            aria-label={t("common:a11y.loading")}
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles["error-container"]}>
          <p>{t("components:carousel.loadError")}</p>
        </div>
      );
    }

    return (
      <div className={styles["scroll-container"]} ref={scrollContainerRef}>
        {items.map((item, index) => (
          <div key={index} className={styles["scroll-item"]}>
            {item}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles["carousel-wrapper"]} ref={ref}>
      <div className={styles["carousel-header"]}>
        {title && title}
        {showCarouselButtons && (canScrollLeft || canScrollRight) && (
          <CarouselButtonGroup
            onPrev={handleScrollLeft}
            onNext={handleScrollRight}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
          />
        )}
      </div>

      {renderContent()}
    </div>
  );
};
